import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ImportContainer } from 'app/Model/dataProcess/importContainer';
import { Workflow } from 'app/Model/Dto/Workflow/Workflow';
import { WorkflowItem } from 'app/Model/Dto/Workflow/WorkflowItem';
import { ImportContainerService } from 'app/Services/DataProcess/import-container.service';
import { LoginService } from 'app/Services/login.service';
import { SystemService } from 'app/Services/system.service';
import { WorkflowService } from 'app/Services/Workflow/workflow.service';
import { custom } from 'devextreme/ui/dialog';

@Component({
    selector: 'np-dataprocess-workflow',
    templateUrl: './dataprocess-workflow.component.html',
    styleUrl: './dataprocess-workflow.component.css',
    standalone: false
})
export class DataprocessWorkflowComponent implements OnInit {
    @Input() public container: ImportContainer;

    public workflows: Workflow[] = [];
    public selectedWorkflow: Workflow;

    public isLoading: boolean = false;
    public isReadOnly: boolean = false;

    public isWorkflowEditorVisible: boolean = false;
    public workflowEditorDraft: Workflow;
    public workflowEditorTitle: string = '';

    public constructor(
        private readonly workflowService: WorkflowService,
        private readonly importContainerService: ImportContainerService,
        private readonly loginService: LoginService,
        private readonly systemService: SystemService,
        private readonly translateService: TranslateService
    ) {
    }

    public async ngOnInit(): Promise<void> {
        await this.loadWorkflows();
        if (this.container?.workflowId) {
            this.selectedWorkflow = this.findWorkflowById(this.container.workflowId);
        }
    }

    public async onSelectedWorkflowChanged(selectedWorkflowId: string): Promise<void> {
        this.selectedWorkflow = this.findWorkflowById(selectedWorkflowId);
        await this.saveWorkflowToContainer(selectedWorkflowId);
    }

    private async saveWorkflowToContainer(workflowId: string): Promise<void> {
        const customerId = this.loginService.currentCustomer?.id;
        if (!customerId || !this.container?.id)
            return;

        this.container.workflowId = workflowId ?? undefined;
        try {
            await this.importContainerService.update(this.container, customerId);
        } catch {
            this.systemService.notifyInfo(this.translateService.instant('Fehler beim Speichern des Workflows'));
        }
    }

    public openCreateWorkflowDialog(): void {
        const workflowName = this.translateService.instant('Workflownamen vergeben');

        this.workflowEditorDraft = new Workflow(null, workflowName);
        this.workflowEditorTitle = this.translateService.instant('Neuer Workflow');
        this.isWorkflowEditorVisible = true;
    }

    public openDuplicateWorkflowDialog(): void {
        if (!this.selectedWorkflow)
            return;

        const sourceWorkflow = this.selectedWorkflow;
        const duplicatedWorkflowName = `${sourceWorkflow.name}${this.translateService.instant('Duplikat')}`;

        this.workflowEditorDraft = new Workflow(undefined, duplicatedWorkflowName, sourceWorkflow.items, false);
        this.workflowEditorTitle = this.translateService.instant('Workflow duplizieren');
        this.isWorkflowEditorVisible = true;
    }

    public async addWorkflowItemToSelectedWorkflow(): Promise<void> {
        const customerId = this.loginService.currentCustomer?.id;
        if (!customerId)
            return;
        if (!this.selectedWorkflow)
            return;

        const selectedWorkflowItems: WorkflowItem[] = this.selectedWorkflow.items ?? [];

        const nextOrder = this.calculateNextOrder(selectedWorkflowItems);
        const newWorkflowItem = new WorkflowItem(nextOrder);

        this.selectedWorkflow.items = [...selectedWorkflowItems, newWorkflowItem];

        this.isLoading = true;
        try {
            await this.workflowService.updateOrCreateWorkflow(this.selectedWorkflow, customerId);
            await this.loadWorkflows();
            this.selectedWorkflow = this.findWorkflowById(this.container.workflowId);
        } catch {
            this.systemService.notifyInfo(this.translateService.instant('Fehler beim Hinzufügen des Workflowitems'));
        } finally {
            this.isLoading = false;
        }
    }

    public async deleteSelectedWorkflow(): Promise<void> {
        const customerId = this.loginService.currentCustomer?.id;
        if (!customerId)
            return;
        if (!this.selectedWorkflow)
            return;

        const selectedWorkflowName = this.selectedWorkflow.name ?? '';

        const confirmDialog = custom({
            title: this.translateService.instant('Wirklich löschen?'),
            messageHtml: `${this.translateService.instant('Wollen Sie den Workflow wirklich löschen')}? <br/><br/><b>${selectedWorkflowName}</b>`,
            buttons: [
                {
                    text: this.translateService.instant('Ja'),
                    onClick: () => true
                },
                {
                    text: this.translateService.instant('Nein'),
                    onClick: () => false
                }
            ]
        });

        if (!await confirmDialog.show())
            return;

        this.isLoading = true;
        try {
            await this.workflowService.deleteWorkflow(this.selectedWorkflow, customerId);

            this.container.workflowId = null;
            this.selectedWorkflow = null;

            await this.loadWorkflows();
        } catch {
            this.systemService.notifyInfo(this.translateService.instant('Fehler beim Löschen des Workflows'));
        } finally {
            this.isLoading = false;
        }
    }

    public async saveWorkflowEditorDraft(): Promise<void> {
        const customerId = this.loginService.currentCustomer?.id;
        if (!customerId || !this.workflowEditorDraft)
            return;

        this.isLoading = true;
        try {
            const savedWorkflow = await this.workflowService.updateOrCreateWorkflow(this.workflowEditorDraft, customerId);

            await this.loadWorkflows();

            const savedWorkflowId = savedWorkflow?.id ?? this.workflows.find((workflow) => workflow?.name === savedWorkflow?.name)?.id;

            this.container.workflowId = savedWorkflowId;
            this.selectedWorkflow = this.findWorkflowById(savedWorkflowId);

            this.isWorkflowEditorVisible = false;
            this.workflowEditorDraft = null;
        } catch {
            this.systemService.notifyInfo(this.translateService.instant('Fehler beim Speichern des Workflows'));
        } finally {
            this.isLoading = false;
        }
    }

    public cancelWorkflowEditorDraft(): void {
        this.isWorkflowEditorVisible = false;
        this.workflowEditorDraft = null;
    }

    private calculateNextOrder(workflowItems: WorkflowItem[]): number {
        if (!Array.isArray(workflowItems) || workflowItems.length === 0)
            return 1;

        const maxOrder = Math.max(
            ...workflowItems.map((workflowItem) =>
                typeof workflowItem?.order === 'number' ? workflowItem.order : 0
            )
        );

        return maxOrder + 1;
    }

    private async loadWorkflows(): Promise<void> {
        const customerId = this.loginService.currentCustomer?.id;

        if (!customerId) {
            this.workflows = [];
            this.container.workflowId = null;
            this.selectedWorkflow = null;
            return;
        }

        this.isLoading = true;
        try {
            const result = await this.workflowService.getWorkflows(customerId);

            this.workflows = result.map((workflow) => ({
                ...workflow,
                items: workflow.items ?? []
            }));

            if (this.container.workflowId) {
                this.selectedWorkflow = this.findWorkflowById(this.container.workflowId);
            }
        } catch {
            this.workflows = [];
            this.container.workflowId = null;
            this.selectedWorkflow = null;
        } finally {
            this.isLoading = false;
        }
    }

    private findWorkflowById(workflowId: string | null): Workflow | null {
        if (!workflowId)
            return null;
        return this.workflows.find((workflow) => workflow?.id === workflowId);
    }

    public openEditWorkflowDialog(): void {
        if (!this.selectedWorkflow)
            return;

        this.workflowEditorDraft = {
            ...(this.selectedWorkflow as any),
            items: this.selectedWorkflow.items ?? []
        };
        this.workflowEditorTitle = this.translateService.instant('Workflow bearbeiten');
        this.isWorkflowEditorVisible = true;
    }
}
